import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  inject,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  IDashcam,
  IFilter,
  IListado,
  IPopulate,
  IQueryParam,
  IReporteGenerico,
} from 'modelos/src';
import { Subscription } from 'rxjs';
import { AuxiliaresModule } from '../../../../../auxiliares/auxiliares.module';
import { DialogService } from '../../../../../auxiliares/dialog/dialog.service';
import { escaparHtml } from '../../../../../auxiliares/html-seguro.util';
import { ClienteJerarquiaService } from '../../../../../auxiliares/servicios/cliente-jerarquia.service';
import { FormularioDialogService } from '../../../../../auxiliares/servicios/formulario-dialog.service';
import { HelperService } from '../../../../../auxiliares/servicios/helper.service';
import { DashcamsService } from '../../../../../auxiliares/servicios/http/dashcams.service';
import { Lista2Service } from '../../../../../auxiliares/servicios/lista2.service';
import {
  estadoConexionCelda,
  fechaCelda,
} from '../../../../../auxiliares/tabla/celdas';
import {
  IFiltroTabla,
  IRegExpSearch,
} from '../../../../../auxiliares/tabla/filtro/filtro.component';
import {
  IColumnas,
  ICrearBoton,
} from '../../../../../auxiliares/tabla/tabla.component';
import {
  CrearEditarDashcamComponent,
  ICrearEditarDashcamData,
} from '../crear-editar-dashcam/crear-editar-dashcam.component';

/** Tipo de reporte de posición de las dashcams (lo escribe el servidor). */
export const TIPO_REPORTE_DASHCAM = 'Dashcam 4G';

interface IDashcamTabla extends IDashcam {
  ultimoReporte?: IReporteGenerico;
}

/** Ícono de la categoría del activo, igual que en el listado de trackers. */
export function iconoActivo(categoria?: string): string {
  if (categoria === 'Vehículo') return 'directions_car';
  if (categoria === 'Colectivo') return 'directions_bus';
  return 'assistant_navigation';
}

@Component({
  selector: 'app-listado-dashcams',
  standalone: true,
  imports: [AuxiliaresModule],
  templateUrl: './listado-dashcams.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ListadoDashcamsComponent implements OnInit, OnDestroy {
  helper = inject(HelperService);
  private service = inject(DashcamsService);
  private lista2 = inject(Lista2Service);
  private clienteJerarquia = inject(ClienteJerarquiaService);
  private dialogService = inject(DialogService);
  private formularios = inject(FormularioDialogService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private cdr = inject(ChangeDetectorRef);

  public name = 'ListadoDashcamsComponent';
  public datos?: IDashcamTabla[];
  public totalCount = 0;
  public columnas?: IColumnas<IDashcamTabla>[];
  private dashcams$?: Subscription;
  private reportes$?: Subscription;

  public botonCrear: ICrearBoton = {
    mostrar: this.service.puedeCrear(),
    tooltip: 'Crear dashcam',
    color: 'primary',
    tipo: 'material',
    icon: 'add',
    accion: () => this.crear(),
  };

  public search: IRegExpSearch = {
    fields: ['uniqueId', 'nombre', 'identificacion', 'numeroSerie'],
  };

  // FILTROS
  private cliente: IFiltroTabla = {
    filter: { field: 'idCliente' },
    label: 'Cliente',
    tipo: 'cliente-selector',
  };
  // Cliente en cuya jerarquía cuelga la dashcam: el elegido y su descendencia.
  private ancestro: IFiltroTabla = {
    filter: { field: 'idsAncestros', incluirPropio: true },
    label: 'En la jerarquía de',
    tipo: 'cliente-selector',
  };
  private asignada: IFiltroTabla = {
    elementos: [
      { label: 'Sí', value: [{ asignadoA: { $ne: null } }] },
      { label: 'No', value: [{ asignadoA: { $eq: null } }] },
    ],
    filter: { field: '$and' },
    label: 'Asignada',
    tipo: 'ngselect',
    selectLabel: 'label',
    selectValue: 'value',
  };
  private habilitado: IFiltroTabla = {
    elementos: [
      {
        label: 'Habilitada',
        icon: 'check',
        iconColor: 'var(--color-green-text)',
        filter: [{ estadoCuenta: null }, { estadoCuenta: 'Habilitado' }],
      },
      {
        label: 'Suspendida',
        icon: 'block',
        iconColor: 'var(--color-red-text)',
        filter: [{ estadoCuenta: 'Suspendido' }],
      },
    ],
    filter: { field: '$or' },
    label: 'Estado de la cuenta',
    rapido: true,
    tipo: 'ngselect',
    selectIcon: true,
    selectLabel: 'label',
    selectValue: 'filter',
  };
  private modelo: IFiltroTabla = {
    filter: { field: 'idModelo' },
    label: 'Modelo',
    tipo: 'modelo-dispositivo-selector',
    tipoDispositivo: 'Dashcam',
  };

  public filtros: IFiltroTabla[] = [
    this.asignada,
    this.habilitado,
    this.modelo,
  ];

  private populate: IPopulate[] = [
    { path: 'cliente', select: 'nombre' },
    { path: 'modelo', select: 'marca modelo' },
    { path: 'activo', select: 'categoria identificacion' },
  ];
  public query: IQueryParam = {
    filter: JSON.stringify({}),
    populate: JSON.stringify(this.populate),
    sort: 'nombre',
    page: 0,
    limit: +this.helper.getPageSize(this.name),
    includeChildren: true,
  };

  /// Listado

  public listar(): void {
    this.dashcams$?.unsubscribe();
    this.reportes$?.unsubscribe();
    this.dashcams$ = this.lista2
      .dashcams(this.query, 30)
      .subscribe((data) => this.completarUltimoReporte(data));
  }

  /** Último reporte de posición de cada dashcam de la página. */
  private completarUltimoReporte(listado: IListado<IDashcamTabla>): void {
    if (!listado.datos.length) {
      this.datos = [];
      this.totalCount = 0;
      this.cdr.markForCheck();
      return;
    }
    const filter: IFilter<IReporteGenerico> = {
      idEntidad: { $in: listado.datos.map((d) => d._id) },
      tipoReporte: TIPO_REPORTE_DASHCAM,
    } as IFilter<IReporteGenerico>;
    const query: IQueryParam = {
      filter: JSON.stringify(filter),
      sort: '-fechaCreacion',
      includeChildren: true,
    };
    this.reportes$?.unsubscribe();
    this.reportes$ = this.lista2
      .ultimosReportesGenericos(query)
      .subscribe((data) => {
        for (const dashcam of listado.datos) {
          dashcam.ultimoReporte = data.datos.find(
            (r) => r.idEntidad === dashcam._id,
          ) as IReporteGenerico | undefined;
        }
        if (HelperService.cambiaronLosDatos(this.datos, listado.datos)) {
          this.totalCount = listado.totalCount;
          this.datos = listado.datos;
          this.cdr.markForCheck();
        }
      });
  }

  /// Acciones

  public crear(): void {
    this.formularios
      .abrir<CrearEditarDashcamComponent, ICrearEditarDashcamData>(
        CrearEditarDashcamComponent,
        { tamano: 'grande', data: {} },
      )
      .afterClosed()
      .subscribe((res) => {
        if (res) this.listar();
      });
  }

  public editar(dashcam: IDashcam): void {
    this.formularios
      .abrir<CrearEditarDashcamComponent, ICrearEditarDashcamData>(
        CrearEditarDashcamComponent,
        { tamano: 'grande', data: { edit: dashcam } },
      )
      .afterClosed()
      .subscribe((res) => {
        if (res) this.listar();
      });
  }

  public detalles(dashcam: IDashcam): void {
    this.router.navigate(['detalles', dashcam._id], { relativeTo: this.route });
  }

  public async eliminar(dashcam: IDashcam): Promise<void> {
    const asignada = dashcam.activo?.identificacion
      ? ` Está asignada a ${dashcam.activo.identificacion}: el activo queda sin dashcam.`
      : '';
    const ok = await this.dialogService.confirm(
      'Eliminar dashcam',
      `¿Querés eliminar la dashcam ${DashcamsService.nombre(dashcam)}?${asignada}`,
    );
    if (!ok) return;
    try {
      await this.service.delete(dashcam._id!);
      this.helper.notifSuccess('Dashcam eliminada');
      this.listar();
    } catch (error) {
      console.error(error);
      this.helper.notifError(error);
    }
  }

  /// Tabla

  /** Dato con su ícono (clase común de la tabla). */
  private linea(texto: string, icono: string, clase = ''): string {
    return `<div class="tabla-linea"><span class="material-symbols-outlined">${icono}</span><span class="${clase}">${texto}</span></div>`;
  }

  private setColumnas(): void {
    const columnas: IColumnas<IDashcamTabla>[] = [
      // Nombre, identificación e ID único
      {
        header: { label: 'Dashcam', sort: 'nombre', sortable: true },
        row: {
          html: (dato) => {
            const lineas: string[] = [];
            const nombre = dato.nombre?.trim();
            const identificacion = dato.identificacion?.trim();
            if (nombre) {
              lineas.push(
                `<span class="tabla-principal">${escaparHtml(nombre)}</span>`,
              );
            }
            if (identificacion && identificacion !== nombre) {
              lineas.push(
                `<span class="tabla-secundario">${escaparHtml(identificacion)}</span>`,
              );
            }
            if (dato.uniqueId) {
              lineas.push(
                this.linea(
                  escaparHtml(dato.uniqueId),
                  'fingerprint',
                  'tabla-codigo',
                ),
              );
            }
            if (!lineas.length) return '';
            return `<div class="tabla-celda-col">${lineas.join('')}</div>`;
          },
          tooltip: (dato) => {
            const items: string[] = [];
            if (dato.nombre)
              items.push(
                `<div>Nombre: <strong>${escaparHtml(dato.nombre)}</strong></div>`,
              );
            if (dato.identificacion)
              items.push(
                `<div>Identificación: <strong>${escaparHtml(dato.identificacion)}</strong></div>`,
              );
            if (dato.uniqueId)
              items.push(
                `<div>ID único: <strong>${escaparHtml(dato.uniqueId)}</strong></div>`,
              );
            if (dato.numeroSerie)
              items.push(
                `<div>Número de serie: <strong>${escaparHtml(dato.numeroSerie)}</strong></div>`,
              );
            return items.length ? items.join('') : undefined;
          },
          noData: 'Sin nombre',
        },
      },

      // Modelo cargado y lo que informó la cámara (modelo y firmware)
      {
        header: { label: 'Equipo' },
        row: {
          html: (dato) => {
            const lineas: string[] = [];
            const modelo = [dato.modelo?.marca, dato.modelo?.modelo]
              .filter(Boolean)
              .join(' - ');
            if (modelo) lineas.push(this.linea(escaparHtml(modelo), 'memory'));
            const informado = dato.equipo?.modelo;
            const firmware = dato.equipo?.versionFirmware;
            if (informado || firmware) {
              const texto = [informado, firmware ? `Firmware ${firmware}` : '']
                .filter(Boolean)
                .join(' · ');
              lineas.push(
                `<span class="tabla-secundario">${escaparHtml(texto)}</span>`,
              );
            }
            if (!lineas.length) return '';
            return `<div class="tabla-celda-col">${lineas.join('')}</div>`;
          },
          tooltip: (dato) => {
            const e = dato.equipo;
            const items: string[] = [];
            const modelo = [dato.modelo?.marca, dato.modelo?.modelo]
              .filter(Boolean)
              .join(' - ');
            if (modelo)
              items.push(
                `<div>Modelo cargado: <strong>${escaparHtml(modelo)}</strong></div>`,
              );
            if (e?.fabricante)
              items.push(
                `<div>Fabricante: <strong>${escaparHtml(e.fabricante)}</strong></div>`,
              );
            if (e?.modelo)
              items.push(
                `<div>Modelo informado: <strong>${escaparHtml(e.modelo)}</strong></div>`,
              );
            if (e?.versionFirmware)
              items.push(
                `<div>Firmware: <strong>${escaparHtml(e.versionFirmware)}</strong></div>`,
              );
            if (e?.imei)
              items.push(
                `<div>IMEI: <strong>${escaparHtml(e.imei)}</strong></div>`,
              );
            return items.length ? items.join('') : undefined;
          },
          noData: 'Sin datos del equipo',
        },
      },

      // Activo al que está asignada
      {
        header: { label: 'Asignación' },
        row: {
          html: (dato) => {
            const activo = dato.activo;
            if (!activo?.identificacion) return '';
            return this.linea(
              escaparHtml(activo.identificacion),
              iconoActivo(activo.categoria),
              'tabla-principal',
            );
          },
          tooltip: (dato) =>
            dato.activo
              ? `<div>Nombre: <strong>${escaparHtml(dato.activo.identificacion)}</strong></div>` +
                `<div>Tipo: <strong>${escaparHtml(dato.activo.categoria)}</strong></div>`
              : undefined,
          noData: 'Sin asignar',
        },
      },

      // Estado de la cuenta, con la fecha de alta debajo
      {
        header: { label: 'Estado de la cuenta' },
        row: {
          estado: (dato) => {
            if (!dato.estadoCuenta) return undefined;
            const habilitado = dato.estadoCuenta === 'Habilitado';
            const alta = fechaCelda(dato.fechaAlta);
            return {
              texto: dato.estadoCuenta,
              tono: habilitado ? 'ok' : 'error',
              icono: habilitado ? 'toggle_on' : 'block',
              detalle: alta ? `Alta ${alta.slice(0, 10)}` : undefined,
              tooltip: alta ? `Fecha de alta: ${alta}` : undefined,
            };
          },
          noData: 'Sin estado de cuenta',
        },
      },

      // Comunicación: mismo criterio que trackers y que el detalle
      {
        header: { label: 'Última comunicación', ancho: 1.4 },
        row: {
          estado: (dato) =>
            estadoConexionCelda(
              dato.ultimoReporte?.fechaCreacion,
              dato.frecReporte,
            ),
          noData: 'Sin comunicación',
        },
      },
    ];

    if (HelperService.getTipoCliente() !== 'Final') {
      columnas.push({
        header: { label: 'Cliente' },
        row: {
          html: (dato) => this.clienteJerarquia.htmlNombre(dato.cliente),
          tooltip: (dato) =>
            this.clienteJerarquia.tooltipJerarquia(dato.cliente),
          noData: 'Sin cliente',
        },
      });
    }

    // "Cambiar estado de la cuenta" y "Servicio técnico" no están: el estado
    // de cuenta del backend sólo resuelve alarmas, trackers y activos, y no
    // existe todavía el evento técnico de dashcam.
    columnas.push({
      header: { label: 'Acciones' },
      row: {
        accionesGrouped: [
          {
            tipo: 'material',
            icon: 'visibility',
            tooltip: 'Ver detalle',
            click: (dato) => this.detalles(dato),
            disabled: () => !this.service.puedeVer(),
          },
          {
            tipo: 'material',
            icon: 'edit',
            tooltip: 'Editar',
            click: (dato) => this.editar(dato),
            oculta: () => HelperService.getTipoCliente() === 'Final',
            disabled: () => !this.service.puedeEditar(),
          },
          {
            tipo: 'material',
            color: 'warn',
            icon: 'delete',
            tooltip: 'Eliminar',
            click: (dato) => this.eliminar(dato),
            oculta: () => HelperService.getTipoCliente() === 'Final',
            disabled: () => !this.service.puedeEliminar(),
          },
        ],
      },
    });

    this.columnas = columnas;
  }

  /// Hooks

  ngOnInit(): void {
    if (HelperService.getTipoCliente() !== 'Final') {
      this.filtros.push(this.cliente, this.ancestro);
    }
    this.setColumnas();
    this.listar();
  }

  ngOnDestroy(): void {
    this.dashcams$?.unsubscribe();
    this.reportes$?.unsubscribe();
  }
}
